import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, fileUrl } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import RichTextEditor from "@/components/RichTextEditor";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FloppyDisk } from "@phosphor-icons/react";

export default function Profile() {
  const { user, refreshMe } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [siteTitle, setSiteTitle] = useState(user?.site_title || "");
  const [siteTagline, setSiteTagline] = useState(user?.site_tagline || "");
  const [pic, setPic] = useState(user?.profile_picture || null);
  const [about, setAbout] = useState(user?.about_page || "");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");

  const save = async () => {
    setBusy(true); setOk("");
    await api.put("/profile", { name, bio, profile_picture: pic, about_page: about, site_title: siteTitle, site_tagline: siteTagline });
    await refreshMe();
    setOk("Saved.");
    setBusy(false);
    setTimeout(() => setOk(""), 2000);
  };

  return (
    <div>
      <div className="mb-8">
        <div className="eyebrow text-muted-foreground mb-2">Identity</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-black tracking-tight">Profile</h1>
        <p className="mt-2 text-sm text-muted-foreground">This is what readers see at <span className="font-mono">/{user?.username}</span></p>
      </div>

      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-7 space-y-6">
          <div>
            <Label className="eyebrow text-muted-foreground">Display name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none mt-2 h-12" data-testid="profile-name-input" />
          </div>
          <div>
            <Label className="eyebrow text-muted-foreground">Bio (one line)</Label>
            <Input value={bio} onChange={(e) => setBio(e.target.value)} className="rounded-none mt-2 h-12" placeholder="Essayist. Photographer. Reluctant futurist." data-testid="profile-bio-input" />
          </div>
          <div>
            <Label className="eyebrow text-muted-foreground">Site title</Label>
            <Input value={siteTitle} onChange={(e) => setSiteTitle(e.target.value)} className="rounded-none mt-2 h-12" data-testid="profile-site-title-input" />
          </div>
          <div>
            <Label className="eyebrow text-muted-foreground">Site tagline</Label>
            <Input value={siteTagline} onChange={(e) => setSiteTagline(e.target.value)} className="rounded-none mt-2 h-12" data-testid="profile-tagline-input" />
          </div>

          <div>
            <Label className="eyebrow text-muted-foreground mb-2 block">About page</Label>
            <RichTextEditor value={about} onChange={setAbout} placeholder="Write a long-form about page for your readers..." />
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={busy} className="rounded-none bg-foreground text-background hover:bg-foreground/90" data-testid="profile-save-button">
              <FloppyDisk size={14} className="mr-1.5" /> {busy ? "Saving..." : "Save changes"}
            </Button>
            {ok ? <span className="text-sm text-emerald-700 dark:text-emerald-400">{ok}</span> : null}
          </div>
        </div>

        <aside className="col-span-12 lg:col-span-5 space-y-6">
          <ImageUploader value={pic} onChange={setPic} label="Profile picture" testId="avatar-uploader" />
          <div className="border border-border bg-card p-6">
            <div className="eyebrow text-muted-foreground mb-3">Live preview</div>
            <div className="flex items-center gap-4">
              {pic ? (
                <img src={fileUrl(pic)} alt="" className="h-16 w-16 object-cover" />
              ) : (
                <div className="h-16 w-16 bg-secondary grid place-items-center text-muted-foreground font-serif text-2xl">{(name || "A")[0]}</div>
              )}
              <div>
                <div className="font-serif text-xl font-bold leading-tight">{name || "Your name"}</div>
                <div className="text-sm text-muted-foreground">{bio || "A short bio appears here."}</div>
                <div className="font-mono text-xs text-muted-foreground mt-1">/{user?.username}</div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
